import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api, money, productImages, sized } from "@/lib/api";
import { setScrollLock } from "@/lib/lenis";
import { EASE } from "@/components/Reveal";

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    setScrollLock(open);
    if (open) setTimeout(() => input.current?.focus(), 250);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/shop/search", { params: { q } })
        .then((r) => setResults(Array.isArray(r.data) ? r.data : []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          data-lenis-prevent
          className="fixed inset-0 z-[60] overflow-y-auto bg-[#F4F2EE]"
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="mx-auto max-w-[1600px] px-4 pb-16 md:px-8">
            <div className="flex h-16 items-center justify-between md:h-[72px]">
              <p className="label">Search</p>
              <button data-testid="search-close-button" aria-label="Close search" onClick={onClose} className="-mr-2 grid h-11 w-11 place-items-center hover:text-[#E2541B]"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <input
              ref={input}
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to search"
              aria-label="Search products"
              className="font-display w-full border-b border-[#111111] bg-transparent pb-4 pt-6 text-4xl font-bold tracking-[-0.05em] outline-none placeholder:text-[#111111]/25 md:text-7xl"
            />
            <p className="label mt-4 text-[#111111]/60">{loading ? "Searching…" : q.length > 1 ? `${results.length} ${results.length === 1 ? "result" : "results"}` : "Hoodies, tees, pants…"}</p>
            <div className="mt-10 grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-6 md:gap-x-5">
              {results.map((p) => {
                const img = productImages(p)[0];
                return (
                  <Link key={p.id} to={`/products/${p.handle}`} onClick={onClose} data-testid={`search-result-${p.handle}`} className="group block">
                    <div className="aspect-[3/4] overflow-hidden bg-[#EEEBE5]">
                      {img && <img src={sized(img.url, 400)} alt={img.altText || p.title} className="h-full w-full object-contain p-[10%] mix-blend-multiply transition-transform duration-700 group-hover:scale-105" />}
                    </div>
                    <p className="font-display mt-2 text-xs font-medium uppercase">{p.title}</p>
                    <p className="label mt-1 text-[10px]">{money(p.priceRange?.minVariantPrice)}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
